import React, { useEffect, useState } from 'react';
import { Box, Heading, Spinner, Text, Alert, AlertIcon, Link, VStack } from '@chakra-ui/react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useTranslation } from 'react-i18next';
import { getApiBaseUrl } from '../../utils/apiBaseUrl';

const OauthCallbackGoogle = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const [status, setStatus] = useState('loading'); // loading, success, error
  const [error, setError] = useState('');
  const [userType, setUserType] = useState('cliente');
  const [registrationUrl, setRegistrationUrl] = useState('/registro-conductor');
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  useEffect(() => {
    if (status !== 'loading') return;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const errorParam = searchParams.get('error');
    if (errorParam) {
      setStatus('error');
      setError(t('oauth_google_error_denied'));
      return;
    }
    if (!code) {
      setStatus('error');
      setError(t('oauth_google_error_no_code'));
      return;
    }

    const callbackRequestKey = `google:${code}:${state || 'no-state'}`;
    const sessionLockKey = `oauth_callback_lock:${callbackRequestKey}`;
    if (typeof window !== 'undefined') {
      if (window.__overtaxiGoogleOauthCallbackKey === callbackRequestKey) {
        return;
      }

      const sessionLock = window.sessionStorage?.getItem(sessionLockKey);
      if (sessionLock === 'inflight' || sessionLock === 'done') {
        return;
      }

      window.__overtaxiGoogleOauthCallbackKey = callbackRequestKey;
      window.sessionStorage?.setItem(sessionLockKey, 'inflight');
    }

    const API_BASE_URL = getApiBaseUrl();
    const queryParams = new URLSearchParams({ code });
    if (state) {
      queryParams.set('state', state);
    }
    const query = queryParams.toString();

    fetch(`${API_BASE_URL}/api/auth/google/callback?${query}`)
      .then(async res => {
        if (!res.ok) {
          const data = await res.json();
          throw new Error(data.detail || t('oauth_google_error_desconocido'));
        }
        return res.json();
      })
      .then(data => {
        if (typeof window !== 'undefined') {
          window.sessionStorage?.setItem(sessionLockKey, 'done');
        }

        if (data.status === 'needs_registration') {
          setUserType(data.user_type || 'conductor');
          setRegistrationUrl(data.registration_url || '/registro-conductor');
          setStatus('needs_registration');
          return;
        }

        login(data);
        setStatus('success');
        navigate(data.user_type === 'conductor' ? '/conductor' : '/cliente');
      })
      .catch(err => {
        if (typeof window !== 'undefined') {
          window.__overtaxiGoogleOauthCallbackKey = null;
          window.sessionStorage?.removeItem(sessionLockKey);
        }
        setStatus('error');
        setError(err.message);
      });
  }, [login, navigate, searchParams, status, t]);

  return (
    <Box p={4} maxW="400px" mx="auto" mt={10}>
      <Heading as="h2" size="lg" mb={6} textAlign="center">
        {t('oauth_google_titulo')}
      </Heading>
      {status === 'loading' && (
        <Box textAlign="center">
          <Spinner size="xl" />
          <Text mt={4}>{t('oauth_google_procesando')}</Text>
        </Box>
      )}
      {status === 'error' && (
        <Alert status="error" mt={4}>
          <AlertIcon />
          {error}
        </Alert>
      )}
      {status === 'needs_registration' && (
        <VStack spacing={4} align="stretch">
          <Text>{t('oauth_google_register_prompt', { userType })}</Text>
          <Link color="gray.600" href={registrationUrl}>
            {t('oauth_google_register_link')}
          </Link>
        </VStack>
      )}
    </Box>
  );
};

export default OauthCallbackGoogle;